import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Images as ImagesIcon, LayoutGrid, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import type { MaterialAttachmentDTO } from "@app/shared";
import { api, carouselSlideUrl } from "../lib/api";
import { useActiveBrand } from "../lib/brand";
import { AttachmentInput } from "../components/AttachmentInput";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  ImageSourceSelect,
  useImageSourceState,
} from "../components/ImageSourceSelect";
import { useToast } from "../components/Toast";

/** Local "YYYY-MM-DD" for a Date (date-input value). */
function toLocalYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
const DEFAULT_TARGET_DATE = toLocalYmd(new Date(Date.now() + 7 * 86_400_000));

/**
 * Carousel mode: the operator types a topic/brief and AI builds a multi-slide
 * educational carousel (cover → education slides → CTA). Each carousel is a
 * ContentDraft (mode=carousel); the detail page previews + edits every slide.
 */
export function Carousel() {
  const qc = useQueryClient();
  const toast = useToast();
  const accountKey = useActiveBrand();

  const [brief, setBrief] = useState("");
  const [slideCount, setSlideCount] = useState(4);
  const [targetDate, setTargetDate] = useState(DEFAULT_TARGET_DATE);
  const [imageSource, setImageSource] = useImageSourceState();
  const [attachment, setAttachment] = useState<MaterialAttachmentDTO | null>(
    null,
  );

  const listQuery = useQuery({
    queryKey: ["drafts", { accountKey, mode: "carousel" }],
    queryFn: ({ signal }) =>
      api.drafts(
        { accountKey: accountKey ?? undefined, mode: "carousel" },
        signal,
      ),
    enabled: !!accountKey,
    refetchInterval: 8000,
  });
  const carousels = listQuery.data ?? [];

  const generate = useMutation({
    mutationFn: () => {
      if (!accountKey) throw new Error("Pilih brand terlebih dahulu");
      return api.generateCarousel({
        accountKey,
        brief: brief.trim(),
        slideCount,
        targetDate: targetDate || undefined,
        imageSource,
        attachment: attachment
          ? { id: attachment.id, kind: attachment.kind, ext: attachment.ext }
          : undefined,
      });
    },
    onSuccess: () => {
      setAttachment(null);
      toast.success(
        "Carousel sedang dibuat… (beberapa slide, ~1-2 menit). Hasil muncul otomatis di bawah.",
      );
      setBrief("");
      [8000, 20000, 40000, 70000].forEach((ms) =>
        setTimeout(
          () => void qc.invalidateQueries({ queryKey: ["drafts"] }),
          ms,
        ),
      );
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Gagal"),
  });

  const canGenerate =
    !!accountKey && brief.trim().length > 0 && !generate.isPending;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-navy">Carousel</h1>
        <p className="text-sm text-slate-500">
          Konten edukasi multi-slide: cover pancingan → slide penjelasan → CTA. AI
          menyusun semuanya; tiap slide bisa kamu edit & ganti gambar.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Buat carousel</CardTitle>
          <CardDescription>
            Ketik topik/brief. AI membuat cover + beberapa slide isi + slide CTA.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-[10rem_10rem_1fr]">
            <div className="space-y-1.5">
              <Label htmlFor="carousel-date">Tanggal tayang</Label>
              <Input
                id="carousel-date"
                type="date"
                value={targetDate}
                onChange={(e) => setTargetDate(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="slidecount">Jumlah slide isi</Label>
              <Input
                id="slidecount"
                type="number"
                min={1}
                max={8}
                value={slideCount}
                onChange={(e) =>
                  setSlideCount(
                    Math.max(1, Math.min(8, Number(e.target.value) || 4)),
                  )
                }
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="brief">Topik / brief</Label>
            <Textarea
              id="brief"
              rows={4}
              placeholder="mis. Kenali aritmia: gejala, penyebab, dan solusinya di Aritmia Center"
              value={brief}
              disabled={generate.isPending}
              onChange={(e) => setBrief(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_14rem]">
            <p className="self-end text-xs text-slate-500">
              Total slide = jumlah slide isi + cover + CTA. Setelah jadi, tiap
              slide bisa diedit teksnya atau diganti gambarnya (termasuk upload
              diagram sendiri).
            </p>
            <ImageSourceSelect
              id="carousel-image-source"
              value={imageSource}
              onChange={setImageSource}
              disabled={generate.isPending}
            />
          </div>

          <AttachmentInput
            value={attachment}
            onChange={setAttachment}
            disabled={generate.isPending}
            hint="Gambar → jadi foto cover. PDF/teks → jadi bahan/referensi isi carousel."
          />

          <div>
            <Button
              variant="primary"
              onClick={() => generate.mutate()}
              disabled={!canGenerate}
            >
              {generate.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <LayoutGrid className="h-4 w-4" />
              )}
              Generate carousel
            </Button>
          </div>
        </CardContent>
      </Card>

      <div>
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-slate-400">
          Carousel dibuat
        </h2>
        {listQuery.isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-teal" />
          </div>
        ) : carousels.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-sm text-slate-500">
              Belum ada carousel. Buat satu di atas.
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {carousels.map((d) => (
              <Link
                key={d.id}
                to={`/carousel/${d.id}`}
                className="group overflow-hidden rounded-lg border border-slate-200 bg-white transition-shadow hover:shadow-md"
              >
                <div className="relative aspect-[4/5] bg-slate-100">
                  <img
                    src={carouselSlideUrl(d.id, 0, d.updatedAt)}
                    alt=""
                    className="h-full w-full object-cover"
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.visibility =
                        "hidden";
                    }}
                  />
                  <span className="absolute right-2 top-2 flex items-center gap-1 rounded-full bg-navy/85 px-2 py-0.5 text-[11px] font-semibold text-white">
                    <ImagesIcon className="h-3 w-3" />
                    {d.slides?.length ?? 0}
                  </span>
                </div>
                <div className="p-2.5">
                  <p className="line-clamp-2 text-sm font-medium text-navy">
                    {d.headline ?? "—"}
                  </p>
                  <p className="mt-0.5 text-xs capitalize text-slate-400">
                    {d.status}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
